import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { v2 } from '../paths';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { getIssueNotice, getIssueOverview, getUnrecognised, IssueEnvironment, IssueNotice, markNoticeSent, UnrecognisedError } from '../../api/issues';
import { ago, number, when } from '../data/format';
import { apiError, usePoll } from '../data/usePoll';
import { copyText } from '../data/clipboard';
import { Card, CheckRow, Empty, EnvChip, ErrorNotice, Kv, Loading } from '../components/ui';

/** What the skill c2e-research-error reads: the error and its examples, without customer names. */
const forSkill = (u: UnrecognisedError) =>
    JSON.stringify(
        {
            code: u.code,
            pattern: u.pattern,
            errorType: u.errorType,
            objectTypes: u.objectTypes,
            eventTypes: u.eventTypes,
            locations: u.locations,
            objects: u.objects,
            samples: u.samples.slice(0, 3),
            stackTrace: u.stackTrace,
        },
        null,
        2
    );

const Unknown = () => {
    const axios = useAxiosSwagger();
    const list = usePoll<UnrecognisedError[]>(axios ? (s) => getUnrecognised(axios, 90, undefined, s) : null, 60000, [axios]);
    // By code: the list sorts again on every refresh, so an index would jump to another error.
    const [sel, setSel] = useState<string>();
    const [copied, setCopied] = useState(false);
    const rows = [...(list.data ?? [])].sort((a, b) => b.open - a.open || b.objects - a.objects);
    const u = rows.find((r) => r.code === sel) ?? rows[0];

    return (
        <div className="g2" style={{ gridTemplateColumns: 'minmax(280px, 0.8fr) minmax(0, 1.5fr)' }}>
            <Card title="Nog geen regel" right={<span className="mut" style={{ fontSize: 12 }}>meeste open boven</span>}>
                <ErrorNotice error={list.error} />
                {list.pending && <Loading>Fouten zonder regel ophalen…</Loading>}
                {list.data && rows.length === 0 && <Empty>Elke fout heeft een regel.</Empty>}
                {rows.map((r) => (
                    <button key={r.code} className={`li ${r.code === u?.code ? 'on' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) 44px' }} onClick={() => { setSel(r.code); setCopied(false); }}>
                        <span>
                            <span className="t mono" style={{ fontSize: 12, fontWeight: 500, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.pattern}</span>
                            <span className="m"><span>{r.environments.slice(0, 2).join(', ')}{r.environments.length > 2 ? ` +${r.environments.length - 2}` : ''}</span><span>{ago(r.lastFailedAt)} geleden</span></span>
                        </span>
                        <span className="mono" style={{ textAlign: 'right' }}>{r.open}</span>
                    </button>
                ))}
            </Card>
            {u ? (
                <Card title={<div className="h2">Fout zonder uitleg</div>} right={<span className="chip">{u.objects} objecten</span>}>
                    <div className="cb stack">
                        <div className="g2e">
                            <Kv rows={[['Waar', u.environments.join(', ')], ['Eerst gezien', when(u.firstFailedAt)], ['Laatst', when(u.lastFailedAt)]]} />
                            <Kv rows={[['Soort', u.objectTypes.join(', ')], ['Plek', <span className="mono">{u.locations[0] ?? '–'}</span>], ['Pogingen', number(u.attempts)]]} />
                        </div>
                        <div>
                            <div className="eyebrow" style={{ marginBottom: 6 }}>Melding, zonder ids</div>
                            <div className="codebox">{u.pattern}</div>
                        </div>
                        <div className="checks">
                            <CheckRow tone={copied ? 'ok' : 'todo'} mark="1" label="Kopieer de fout voor de skill" value="c2e-research-error" />
                            <CheckRow tone="todo" mark="2" label="Regel en klanttekst laten schrijven" value="ErrorInterpreter" />
                            <CheckRow tone="todo" mark="3" label="Checker erbij, zodat de scanner het zelf naloopt" value="Issues/Checks" />
                        </div>
                        <div className="hrow">
                            <button className="btn pri" onClick={() => setCopied(copyText(forSkill(u)))}>
                                {copied ? 'Gekopieerd' : 'Kopieer voor de skill'}
                            </button>
                        </div>
                    </div>
                </Card>
            ) : rows.length > 0 ? (
                <Empty>Kies links een fout.</Empty>
            ) : (
                <span />
            )}
        </div>
    );
};

const Mails = () => {
    const axios = useAxiosSwagger();
    // Opened from an issue with ?klant=&omgeving=: that customer first.
    const [params] = useSearchParams();
    const overview = usePoll<IssueEnvironment[]>(axios ? (s) => getIssueOverview(axios, s) : null, 60000, [axios]);
    const waiting = (overview.data ?? []).filter((e) => e.customerNotTold > 0).sort((a, b) => b.customerNotTold - a.customerNotTold);
    const [sel, setSel] = useState<IssueEnvironment>();
    const [notice, setNotice] = useState<IssueNotice>();
    const [text, setText] = useState('');
    const [done, setDone] = useState<string>();
    const [failed, setFailed] = useState<string>();
    const [copied, setCopied] = useState(false);
    const asked = waiting.find(
        (e) => e.customer.toLowerCase() === (params.get('klant') ?? '').toLowerCase() && e.environment.toLowerCase() === (params.get('omgeving') ?? '').toLowerCase()
    );
    const current = sel ?? asked ?? waiting[0];
    // Keyed on the names, not the object: the overview comes in again every minute, and a new object would draft the
    // mail again over what was typed.
    const currentKey = current ? `${current.customer}/${current.environment}` : '';

    useEffect(() => {
        if (!axios || !current) return;
        setNotice(undefined);
        setDone(undefined);
        setFailed(undefined);
        setCopied(false);
        const controller = new AbortController();
        getIssueNotice(axios, current.customer, current.environment, false, controller.signal)
            .then((n) => {
                setNotice(n);
                setText(n.body ?? '');
            })
            .catch((e) => {
                if (!controller.signal.aborted) setFailed(apiError(e));
            });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axios, currentKey]);

    const sent = async () => {
        if (!axios || !current || !notice) return;
        try {
            await markNoticeSent(axios, current.customer, current.environment, { keys: notice.keys, language: notice.language, subject: notice.subject, proposed: notice.body, sent: text });
            setFailed(undefined);
            setDone('Gemarkeerd als verstuurd');
        } catch (e) {
            setFailed(apiError(e));
            return;
        }
        overview.reload();
    };

    return (
        <div className="g2" style={{ gridTemplateColumns: 'minmax(260px, 0.7fr) minmax(0, 1.6fr)' }}>
            <Card title="Klaar om te sturen">
                <ErrorNotice error={overview.error} />
                {overview.pending && <Loading>Klanten ophalen…</Loading>}
                {overview.data && waiting.length === 0 && <Empty>Geen klant hoeft nu een mail.</Empty>}
                {waiting.map((e) => (
                    <button key={`${e.customer}/${e.environment}`} className={`li ${current?.customer === e.customer && current?.environment === e.environment ? 'on' : ''}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }} onClick={() => setSel(e)}>
                        <span>
                            <span className="t">{e.customer}</span>
                            <span className="m"><EnvChip kind={e.stage} environment={e.environment} /><span>{e.customerNotTold} nog niet verteld</span></span>
                        </span>
                        <span className="own cu">Klant</span>
                    </button>
                ))}
            </Card>
            {current ? (
                <Card title={<div className="h2">Mail aan {current.customer} <EnvChip kind={current.stage} environment={current.environment} /></div>} right={<span className="chip">{notice?.language ?? ''}</span>}>
                    <div className="cb stack">
                        {!notice && !failed && <Loading>Mail opstellen…</Loading>}
                        {!notice && failed && <ErrorNotice error={failed} />}
                        {notice && (
                            <>
                                <Kv rows={[['Aan', notice.emailAddress ?? 'contactpersoon uit Chargebee'], ['Onderwerp', notice.subject ?? '–'], ['Over', `${notice.keys.length} objecten`]]} />
                                {!notice.needed && <div className="mut">{notice.reason}</div>}
                                <textarea className="mailbox" style={{ minHeight: 260, border: 0, fontFamily: 'inherit' }} value={text} onChange={(e) => setText(e.target.value)} aria-label="Tekst van de mail" />
                                <div className="hrow">
                                    <button className="btn pri" onClick={() => setCopied(copyText(text))}>{copied ? 'Gekopieerd' : 'Kopiëren'}</button>
                                    <button className="btn" onClick={sent}>Markeer als verstuurd</button>
                                    {done && <span className="ok">{done}</span>}
                                    {failed && <span className="bad">{failed}</span>}
                                </div>
                            </>
                        )}
                    </div>
                </Card>
            ) : waiting.length > 0 ? (
                <Empty>Kies links een klant.</Empty>
            ) : (
                <span />
            )}
        </div>
    );
};

/** Each tab has its own path (see v2.learn), so the rail marks only the open one. */
export const LearnPage = ({ tab = 'unknown' }: { tab?: 'unknown' | 'mails' }) => {
    return (
        <>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">Leren</div>
                    <h1 className="h1">{tab === 'mails' ? 'Klantmails' : 'Onbekende fouten'}</h1>
                </div>
            </div>
            <div className="tabs" role="tablist">
                <Link className={tab === 'unknown' ? 'tab on' : 'tab'} to={v2.learn()}>Onbekende fouten</Link>
                <Link className={tab === 'mails' ? 'tab on' : 'tab'} to={v2.learn('mails')}>Klantmails</Link>
            </div>
            {tab === 'unknown' ? <Unknown /> : <Mails />}
        </>
    );
};
